import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
} from "react";
import { gql } from "@apollo/client";
import { useDropzone } from "react-dropzone";
import { UploadCloud, ChevronLeft, Check, Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@src/lib/ui/Dialog";
import { Button } from "@src/lib/ui/Button";
import { Input } from "@src/lib/ui/Input";
import { Label } from "@src/lib/ui/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@src/lib/ui/Select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@src/lib/ui/Command";
import { Popover, PopoverContent, PopoverTrigger } from "@src/lib/ui/Popover";
import Text from "@src/lib/ui/Text";
import Spinner from "@src/lib/ui/Spinner";
import ChevronDownIcon from "@src/lib/icons/18px/chevron-down";
import { cn } from "@src/lib/utils/cn";
import { useToast } from "@src/lib/hooks/useToast";
import { useS3Upload } from "@src/features/app/hooks/useS3Upload";
import {
  CreateOrgGuestFeedbackCsvUploadInput,
  GuestFeedbackBatchFileInput,
  PresignedS3PostDataBucket,
} from "@src/types.generated";
import {
  useMatchOrgGuestFeedbackCsvUploadLocationsMutation,
  useLocationsForGuestFeedbackQuery,
  useGetCsvColumnsMutation,
  useGetCsvColumnNumberRangeMutation,
  useGuestFeedbackSourceOptionsQuery,
} from "./GuestFeedbackUploadCSVDialog.generated";
import { computeSourceComboboxItems } from "./sourceComboboxItems";

type Step = "upload" | "columns" | "locations";

type ColumnMappings = {
  feedbackTextColumn: string;
  feedbackDateColumn: string;
  ratingColumn: string;
  locationColumn: string;
};

type LocationMapping = {
  locationName: string;
  locationId: number | null;
};

export type CsvMappingDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPrepared: (file: GuestFeedbackBatchFileInput) => void;
  onCancel?: () => void;
  initialFile?: GuestFeedbackBatchFileInput;
  analysisStatusMessage?: string;
  onDirty?: () => void;
  selectedFile?: File;
};

export const GuestFeedbackCsvMappingDialog: React.FC<CsvMappingDialogProps> = ({
  open,
  onOpenChange,
  onPrepared,
  onCancel,
  initialFile,
  analysisStatusMessage,
  onDirty,
  selectedFile,
}) => {
  const { addErrorToast } = useToast();

  const [fileName, setFileName] = useState(initialFile?.fileName ?? "");
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Step state
  const [step, setStep] = useState<Step>("upload");

  // Upload step state
  const [fileUrl, setFileUrl] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);

  // Columns step state
  const [csvColumns, setCsvColumns] = useState<
    { name: string; exampleValues: string[] }[]
  >([]);
  const [columnMappings, setColumnMappings] = useState<ColumnMappings>({
    feedbackTextColumn: "",
    feedbackDateColumn: "",
    ratingColumn: "",
    locationColumn: "",
  });

  // Source label for this upload
  const [feedbackSource, setFeedbackSource] = useState<string>("");

  // Rating range state
  const [ratingRangeOptions, setRatingRangeOptions] = useState<{
    min: number;
    max: number;
  } | null>(null);
  const [ratingMin, setRatingMin] = useState<number | null>(null);
  const [ratingMax, setRatingMax] = useState<number | null>(null);
  const [isLoadingRatingRange, setIsLoadingRatingRange] = useState(false);
  const [ratingRangeError, setRatingRangeError] = useState<string | null>(null);

  // Locations step state
  const [locationMappings, setLocationMappings] = useState<LocationMapping[]>(
    [],
  );
  const [isMatchingLocations, setIsMatchingLocations] = useState(false);

  // GraphQL
  const [getCsvColumns] = useGetCsvColumnsMutation();
  const [getCsvColumnNumberRange] = useGetCsvColumnNumberRangeMutation();
  const [matchLocations] = useMatchOrgGuestFeedbackCsvUploadLocationsMutation();
  const { data: locationsData } = useLocationsForGuestFeedbackQuery({
    variables: { pagination: { limit: 500 } },
    skip: step !== "locations",
  });
  const { data: sourceData } = useGuestFeedbackSourceOptionsQuery({
    skip: !open,
  });

  const locationOptions = useMemo(() => {
    return (
      locationsData?.Locations?.objects?.map((loc) => ({
        id: loc.id,
        name: loc.name,
      })) ?? []
    );
  }, [locationsData]);

  // Source options for the dropdown: the org's connected integration providers
  // (Ovation / Google Business) plus previously-used CSV source labels.
  const sourceOptions = useMemo(() => {
    const integrationSources: string[] = [];
    for (const integration of sourceData?.OrgGuestFeedbackIntegrations ?? []) {
      if (integration.ovationIntegrationConfig) {
        integrationSources.push("Ovation");
      } else if (integration.googleBusinessConfig) {
        integrationSources.push("Google Business");
      }
    }

    const csvSources =
      sourceData?.OrgGuestFeedbackCsvUploads?.objects
        ?.map((upload) => upload.feedbackSource)
        .filter(
          (source): source is string => !!source && source.trim() !== "",
        ) ?? [];

    // Dedupe case-insensitively, preferring the canonical integration labels.
    const seen = new Set<string>();
    const options: string[] = [];
    for (const source of [...integrationSources, ...csvSources]) {
      const key = source.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        options.push(source);
      }
    }
    return options.sort((a, b) => a.localeCompare(b));
  }, [sourceData]);

  // S3 Upload
  const { handleFileUpload } = useS3Upload({
    bucket: PresignedS3PostDataBucket.Media,
    onUploadComplete: async (upload) => {
      if (!mounted.current) return;
      setFileName(upload.name);
      setFileUrl(upload.url);

      // Fetch CSV columns from server
      try {
        const result = await getCsvColumns({ variables: { url: upload.url } });
        if (
          result.data?.getCsvColumns?.success &&
          result.data.getCsvColumns.columns
        ) {
          setCsvColumns(result.data.getCsvColumns.columns);
          setStep("columns");
        } else {
          addErrorToast({
            message:
              result.data?.getCsvColumns?.error?.message ??
              "Failed to parse CSV file",
            callsite: "guest_feedback_csv_parse",
          });
        }
      } catch {
        addErrorToast({
          message: "Failed to parse CSV file",
          callsite: "guest_feedback_csv_parse",
        });
      } finally {
        setIsUploading(false);
      }
    },
    onUploadFailed: () => {
      setIsUploading(false);
      addErrorToast({
        message: "Failed to upload file",
        callsite: "guest_feedback_csv_upload",
      });
    },
  });

  const handleFileSelect = useCallback(
    (file: File) => {
      onDirty?.();
      setFileName(file.name);
      setIsUploading(true);
      handleFileUpload(file);
    },
    [handleFileUpload, onDirty],
  );

  const uploadedSelection = useRef<File | undefined>(undefined);
  useEffect(() => {
    if (selectedFile && uploadedSelection.current !== selectedFile) {
      uploadedSelection.current = selectedFile;
      handleFileSelect(selectedFile);
    }
  }, [selectedFile, handleFileSelect]);

  const onDrop = useCallback(
    <T extends File>(acceptedFiles: T[]) => {
      if (acceptedFiles.length === 1) {
        handleFileSelect(acceptedFiles[0]);
      }
    },
    [handleFileSelect],
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open: openFilePicker,
  } = useDropzone({
    onDrop,
    onDropRejected: () =>
      addErrorToast({
        message: "Only .csv files are allowed",
        callsite: "guest_feedback_csv_upload",
      }),
    multiple: false,
    noClick: true,
    noKeyboard: true,
    noDragEventsBubbling: true,
    accept: { "text/csv": [".csv"] },
  });

  // Get column headers from csvColumns
  const columnHeaders = useMemo(
    () =>
      csvColumns.filter((col) => col.name.trim() !== "").map((col) => col.name),
    [csvColumns],
  );

  // Get example values for a column
  const getColumnExamples = useCallback(
    (columnName: string) => {
      const column = csvColumns.find((col) => col.name === columnName);
      if (!column || column.exampleValues.length === 0) return "";
      return column.exampleValues.join(", ");
    },
    [csvColumns],
  );

  // Handle rating column change - fetch number range
  const handleRatingColumnChange = useCallback(
    async (value: string) => {
      setColumnMappings((prev) => ({ ...prev, ratingColumn: value }));
      setRatingRangeOptions(null);
      setRatingMin(null);
      setRatingMax(null);
      setRatingRangeError(null);

      if (!value) return;

      setIsLoadingRatingRange(true);
      try {
        const result = await getCsvColumnNumberRange({
          variables: { url: fileUrl, columnName: value },
        });

        if (result.data?.getCsvColumnNumberRange?.success) {
          if (result.data.getCsvColumnNumberRange.numberRange) {
            const { minValue, maxValue } =
              result.data.getCsvColumnNumberRange.numberRange;
            setRatingRangeOptions({ min: minValue, max: maxValue });
            setRatingMin(minValue);
            setRatingMax(maxValue);
          } else {
            // No number values detected in column
            setRatingRangeError("No number values detected in this column");
          }
        } else {
          // Default to 1-5 if fetch fails
          setRatingRangeOptions({ min: 1, max: 5 });
          setRatingMin(1);
          setRatingMax(5);
        }
      } catch {
        // Default to 1-5 if fetch fails
        setRatingRangeOptions({ min: 1, max: 5 });
        setRatingMin(1);
        setRatingMax(5);
      } finally {
        setIsLoadingRatingRange(false);
      }
    },
    [fileUrl, getCsvColumnNumberRange],
  );

  // Check if all columns are selected
  const allColumnsSelected = useMemo(() => {
    return (
      columnMappings.feedbackTextColumn &&
      columnMappings.feedbackDateColumn &&
      columnMappings.ratingColumn &&
      columnMappings.locationColumn &&
      ratingMin !== null &&
      ratingMax !== null
    );
  }, [columnMappings, ratingMin, ratingMax]);

  // Handle moving from columns to locations step
  const handleColumnsNext = useCallback(async () => {
    setIsMatchingLocations(true);

    try {
      const result = await matchLocations({
        variables: {
          input: {
            csvUrl: fileUrl,
            columnName: columnMappings.locationColumn,
          },
        },
      });

      if (result.data?.matchOrgGuestFeedbackCsvUploadLocations?.success) {
        const mappings =
          result.data.matchOrgGuestFeedbackCsvUploadLocations.locationMappings;

        if (mappings.length === 0) {
          addErrorToast({
            message: "No location values found in the selected column",
            callsite: "guest_feedback_csv_locations",
          });
          return;
        }

        // Keep locations the user already mapped (e.g. when reopening a
        // prepared file); only unmapped names take the suggested match.
        setLocationMappings((previous) => {
          const saved = new Map(
            previous.map((m) => [m.locationName, m.locationId]),
          );
          return mappings.map((m) => ({
            locationName: m.locationName,
            locationId: saved.get(m.locationName) ?? m.locationId ?? null,
          }));
        });
        setStep("locations");
      } else {
        addErrorToast({
          message:
            result.data?.matchOrgGuestFeedbackCsvUploadLocations?.error
              ?.message ?? "Failed to match locations",
          callsite: "guest_feedback_csv_match_locations",
        });
      }
    } catch {
      addErrorToast({
        message: "Failed to match locations",
        callsite: "guest_feedback_csv_match_locations",
      });
    } finally {
      setIsMatchingLocations(false);
    }
  }, [fileUrl, columnMappings.locationColumn, matchLocations, addErrorToast]);

  // The org's location ids, once the full list has loaded. A reopened file
  // can carry a saved mapping to a location that no longer exists; the picker
  // shows it as unselected, so it must not count as mapped either.
  const knownLocationIds = useMemo(() => {
    const locations = locationsData?.Locations;
    if (!locations || locations.objects.length < locations.totalCount) {
      return null;
    }
    return new Set(locations.objects.map((loc) => loc.id));
  }, [locationsData]);

  // Check if all locations are mapped
  const allLocationsMapped = useMemo(() => {
    return (
      locationMappings.length > 0 &&
      locationMappings.every(
        (m) =>
          m.locationId !== null &&
          (!knownLocationIds || knownLocationIds.has(m.locationId)),
      )
    );
  }, [locationMappings, knownLocationIds]);

  // Handle location mapping change
  const handleLocationMappingChange = useCallback(
    (locationName: string, newLocationId: number) => {
      setLocationMappings((prev) =>
        prev.map((m) =>
          m.locationName === locationName
            ? { ...m, locationId: newLocationId }
            : m,
        ),
      );
    },
    [],
  );

  useEffect(() => {
    if (!initialFile) return;
    const csv = initialFile.csv;
    setFileUrl(csv.fileUrl);
    setFileName(initialFile.fileName);
    setColumnMappings({
      feedbackTextColumn: csv.feedbackTextColumn,
      feedbackDateColumn: csv.feedbackDateColumn,
      ratingColumn: csv.ratingColumn,
      locationColumn: csv.locationColumn,
    });
    setRatingMin(csv.ratingColumnMin);
    setRatingMax(csv.ratingColumnMax);
    setRatingRangeOptions({
      min: csv.ratingColumnMin,
      max: csv.ratingColumnMax,
    });
    setRatingRangeError(null);
    setFeedbackSource(csv.feedbackSource ?? "");
    setLocationMappings(csv.locationMappings);
    setStep("locations");
    getCsvColumns({ variables: { url: csv.fileUrl } })
      .then((result) => {
        if (mounted.current)
          setCsvColumns(result.data?.getCsvColumns?.columns ?? []);
      })
      .catch(() => undefined);
  }, [initialFile, getCsvColumns]);

  // Handle final submission
  const handleSubmit = useCallback(() => {
    const csv: CreateOrgGuestFeedbackCsvUploadInput = {
      fileUrl,
      ...columnMappings,
      ratingColumnMin: ratingMin!,
      ratingColumnMax: ratingMax!,
      feedbackSource: feedbackSource.trim() || undefined,
      locationMappings: locationMappings.map((m) => ({
        locationName: m.locationName,
        locationId: m.locationId!,
      })),
    };
    onPrepared({ id: initialFile?.id, fileName, csv });
  }, [
    fileUrl,
    columnMappings,
    feedbackSource,
    ratingMin,
    ratingMax,
    locationMappings,
    onPrepared,
    initialFile,
    fileName,
  ]);

  const getDialogTitle = () => {
    switch (step) {
      case "upload":
        return "Upload Guest Feedback CSV";
      case "columns":
        return "Map CSV Columns";
      case "locations":
        return "Map Locations";
    }
  };

  const getDialogDescription = () => {
    switch (step) {
      case "upload":
        return "Upload a CSV file containing guest feedback data";
      case "columns":
        return "Match the CSV columns to the required feedback fields";
      case "locations":
        return "Verify or adjust the location mappings for your feedback data";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl"
        onDragOver={(e: React.DragEvent) => e.preventDefault()}
        onDrop={(e: React.DragEvent) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{getDialogTitle()}</DialogTitle>
          <DialogDescription>{getDialogDescription()}</DialogDescription>
        </DialogHeader>

        {analysisStatusMessage && (
          <p
            role="status"
            className="rounded-lg bg-bg-secondary p-3 text-sm text-text-secondary"
          >
            {analysisStatusMessage}
          </p>
        )}
        {/* Upload Step */}
        {step === "upload" && (
          <div className="flex min-w-0 flex-col gap-4">
            <div
              {...getRootProps()}
              className={cn(
                "flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-12 text-center",
                isDragActive ? "border-blue-6 bg-blue-2" : "border-gray-4",
              )}
            >
              <input {...getInputProps()} />
              {isUploading ? (
                <div className="flex flex-col items-center gap-4">
                  <Spinner className="size-12" />
                  <Text
                    variant="compact-description"
                    className="text-muted-foreground"
                  >
                    Uploading...
                  </Text>
                </div>
              ) : (
                <>
                  <UploadCloud className="mb-4 size-16 text-gray-5" />
                  <Text variant="body-title" className="mb-2">
                    Drag and drop your CSV file here
                  </Text>
                  <Button onClick={openFilePicker}>Browse Files</Button>
                </>
              )}
            </div>
            <div className="rounded-lg border border-gray-2 bg-gray-1 p-4">
              <Text variant="compact-title" className="mb-2">
                CSV requirements
              </Text>
              <Text
                variant="fine-print-description"
                className="text-muted-foreground"
                multiline
              >
                Your CSV needs a header row and a column for each of the
                following — you&apos;ll match the columns on the next step, so
                they can be named anything:
              </Text>
              <ul className="mt-2 flex list-disc flex-col gap-1 pl-5">
                <li>
                  <Text
                    variant="fine-print-description"
                    multiline
                    className="text-muted-foreground"
                  >
                    <span className="font-semibold text-foreground">
                      Feedback
                    </span>{" "}
                    — the review or comment text
                  </Text>
                </li>
                <li>
                  <Text
                    variant="fine-print-description"
                    multiline
                    className="text-muted-foreground"
                  >
                    <span className="font-semibold text-foreground">Date</span>{" "}
                    — when the feedback was left (e.g. 2026-05-28)
                  </Text>
                </li>
                <li>
                  <Text
                    variant="fine-print-description"
                    multiline
                    className="text-muted-foreground"
                  >
                    <span className="font-semibold text-foreground">
                      Rating
                    </span>{" "}
                    — a numeric score; you&apos;ll confirm the scale (e.g. 1–5)
                    on the next step
                  </Text>
                </li>
                <li>
                  <Text
                    variant="fine-print-description"
                    multiline
                    className="text-muted-foreground"
                  >
                    <span className="font-semibold text-foreground">
                      Location
                    </span>{" "}
                    — the location name; each unique value gets matched to one
                    of your Opus locations before importing
                  </Text>
                </li>
              </ul>
            </div>
          </div>
        )}

        {/* Columns Step */}
        {step === "columns" && (
          <div className="flex min-w-0 flex-col gap-6">
            <div className="flex flex-col gap-2 border-b border-gray-3 pb-6">
              <div className="flex items-center justify-between gap-4">
                <div className="flex min-w-0 flex-col">
                  <Label>Source</Label>
                  <Text
                    variant="fine-print-description"
                    multiline
                    className="text-muted-foreground"
                  >
                    Where this feedback data comes from (e.g. Google, Yelp)
                  </Text>
                </div>
                <SourceCombobox
                  value={feedbackSource}
                  options={sourceOptions}
                  onChange={setFeedbackSource}
                />
              </div>
            </div>
            <ColumnSelect
              label="Feedback Column"
              description="The column containing the guest feedback text"
              value={columnMappings.feedbackTextColumn}
              options={columnHeaders}
              onChange={(value) =>
                setColumnMappings((prev) => ({
                  ...prev,
                  feedbackTextColumn: value,
                }))
              }
              examples={getColumnExamples(columnMappings.feedbackTextColumn)}
            />
            <ColumnSelect
              label="Date Column"
              description="The column containing the date of the feedback"
              value={columnMappings.feedbackDateColumn}
              options={columnHeaders}
              onChange={(value) =>
                setColumnMappings((prev) => ({
                  ...prev,
                  feedbackDateColumn: value,
                }))
              }
              examples={getColumnExamples(columnMappings.feedbackDateColumn)}
            />
            <ColumnSelect
              label="Rating Column"
              description="The column containing the rating score"
              value={columnMappings.ratingColumn}
              options={columnHeaders}
              onChange={handleRatingColumnChange}
              examples={getColumnExamples(columnMappings.ratingColumn)}
            />
            {columnMappings.ratingColumn && (
              <div className="flex flex-col gap-2 pl-4">
                <Label>Rating Range</Label>
                {isLoadingRatingRange ? (
                  <div className="flex items-center gap-2">
                    <Spinner className="size-4" />
                    <Text
                      variant="fine-print-description"
                      className="text-muted-foreground"
                    >
                      Detecting rating range...
                    </Text>
                  </div>
                ) : ratingRangeError ? (
                  <Text variant="fine-print-description" className="text-red-9">
                    {ratingRangeError}
                  </Text>
                ) : ratingRangeOptions ? (
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Text
                        variant="fine-print-description"
                        className="text-muted-foreground"
                      >
                        Min
                      </Text>
                      <Input
                        type="number"
                        className="w-20"
                        value={ratingMin ?? ""}
                        onChange={(e) =>
                          setRatingMin(
                            e.target.value ? Number(e.target.value) : null,
                          )
                        }
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Text
                        variant="fine-print-description"
                        className="text-muted-foreground"
                      >
                        Max
                      </Text>
                      <Input
                        type="number"
                        className="w-20"
                        value={ratingMax ?? ""}
                        onChange={(e) =>
                          setRatingMax(
                            e.target.value ? Number(e.target.value) : null,
                          )
                        }
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            )}
            <ColumnSelect
              label="Location Column"
              description="The column identifying the location"
              value={columnMappings.locationColumn}
              options={columnHeaders}
              onChange={(value) =>
                setColumnMappings((prev) => ({
                  ...prev,
                  locationColumn: value,
                }))
              }
              examples={getColumnExamples(columnMappings.locationColumn)}
            />
          </div>
        )}

        {/* Locations Step */}
        {step === "locations" && (
          <div className="flex min-w-0 flex-col gap-4">
            <div className="grid grid-cols-2 gap-4 border-b border-gray-3 pb-2">
              <Text variant="compact-title">CSV Location</Text>
              <Text variant="compact-title">Opus Location</Text>
            </div>
            <div className="flex max-h-80 flex-col gap-4 overflow-y-auto">
              {locationMappings.map((mapping) => (
                <div
                  key={mapping.locationName}
                  className="grid grid-cols-2 items-center gap-4"
                >
                  <Text variant="compact-description" className="truncate">
                    {mapping.locationName}
                  </Text>
                  <LocationCombobox
                    value={mapping.locationId}
                    options={locationOptions}
                    onChange={(value) =>
                      handleLocationMappingChange(mapping.locationName, value)
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        {(step !== "upload" || onCancel) && (
          <DialogFooter className="gap-2 sm:justify-between">
            <div className="flex gap-2">
              {step !== "upload" && !(initialFile && step === "columns") && (
                <Button
                  variant="outline"
                  onClick={() =>
                    setStep(step === "columns" ? "upload" : "columns")
                  }
                >
                  <ChevronLeft className="mr-1 size-4" />
                  Back
                </Button>
              )}
              {onCancel && (
                <Button variant="outline" onClick={onCancel}>
                  {initialFile ? "Cancel changes" : "Cancel file"}
                </Button>
              )}
            </div>
            {step === "columns" && (
              <Button
                onClick={handleColumnsNext}
                disabled={!allColumnsSelected}
                loading={isMatchingLocations}
                loadingText="Matching..."
              >
                Next
              </Button>
            )}
            {step === "locations" && (
              <Button onClick={handleSubmit} disabled={!allLocationsMapped}>
                Next
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Column select sub-component
const ColumnSelect: React.FC<{
  label: string;
  description: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  examples: string;
}> = ({ label, description, value, options, onChange, examples }) => {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 flex-col">
          <Label>{label}</Label>
          <Text
            variant="fine-print-description"
            multiline
            className="text-muted-foreground"
          >
            {description}
          </Text>
        </div>
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger className="w-48 shrink-0">
            <SelectValue placeholder="Select column" />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Text
        variant="fine-print-description"
        className="text-muted-foreground"
        ellipsis
      >
        {examples ? `Examples: ${examples}` : "Select a column to see examples"}
      </Text>
    </div>
  );
};

// Source combobox sub-component: pick a previously-used source or type a new one
const SourceCombobox: React.FC<{
  value: string;
  options: string[];
  onChange: (value: string) => void;
}> = ({ value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const { trimmedSearch, filteredOptions, canCreate } = useMemo(
    () => computeSourceComboboxItems(search, options),
    [search, options],
  );

  const handleSelect = useCallback(
    (selected: string) => {
      onChange(selected);
      setOpen(false);
      setSearch("");
    },
    [onChange],
  );

  // Commit typed-but-unselected text when the popover closes (click away,
  // Escape, trigger click) so it behaves like selecting the "Add" item —
  // otherwise the typed source is silently discarded.
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      setOpen(newOpen);
      if (newOpen) return;
      const trimmed = search.trim();
      if (trimmed) {
        // Prefer the canonical casing of an existing option, matching what
        // selecting it from the list would commit.
        const existing = options.find(
          (opt) => opt.toLowerCase() === trimmed.toLowerCase(),
        );
        onChange(existing ?? trimmed);
      }
      setSearch("");
    },
    [search, options, onChange],
  );

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-9 w-48 justify-between font-normal"
        >
          <span className={cn("truncate", !value && "text-muted-foreground")}>
            {value || "Select or add source..."}
          </span>
          <ChevronDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="z-[9999] w-[var(--radix-popover-trigger-width)] p-0"
        align="end"
        disablePortal
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search or add source..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {filteredOptions.length === 0 && !canCreate && (
              <CommandEmpty>No sources found</CommandEmpty>
            )}
            {filteredOptions.length > 0 && (
              <CommandGroup>
                {filteredOptions.map((opt) => (
                  <CommandItem
                    key={opt}
                    value={opt}
                    onSelect={() => handleSelect(opt)}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        "mr-2 size-4",
                        value === opt ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {opt}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {canCreate && (
              <CommandGroup>
                <CommandItem
                  value={`__add__${trimmedSearch}`}
                  onSelect={() => handleSelect(trimmedSearch)}
                  className="cursor-pointer"
                >
                  <Plus className="mr-2 size-4" />
                  Add &quot;{trimmedSearch}&quot;
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

// Searchable location combobox sub-component
const LocationCombobox: React.FC<{
  value: number | null;
  options: { id: number; name: string }[];
  onChange: (value: number) => void;
}> = ({ value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const searchLower = search.toLowerCase();
    return options.filter((opt) =>
      opt.name.toLowerCase().includes(searchLower),
    );
  }, [options, search]);

  const selectedOption = useMemo(() => {
    if (value === null) return null;
    return options.find((opt) => opt.id === value);
  }, [value, options]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-9 w-full justify-between font-normal"
        >
          <span
            className={cn(
              "truncate",
              !selectedOption && "text-muted-foreground",
            )}
          >
            {selectedOption ? selectedOption.name : "Select location..."}
          </span>
          <ChevronDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="z-[9999] w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        disablePortal
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search locations..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>No locations found</CommandEmpty>
            <CommandGroup>
              {filteredOptions.map((opt) => (
                <CommandItem
                  key={opt.id}
                  value={String(opt.id)}
                  onSelect={() => {
                    onChange(opt.id);
                    setOpen(false);
                    setSearch("");
                  }}
                  className="cursor-pointer"
                >
                  <Check
                    className={cn(
                      "mr-2 size-4",
                      value === opt.id ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {opt.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default GuestFeedbackCsvMappingDialog;

gql`
  mutation GetCsvColumns($url: String!) {
    getCsvColumns(url: $url) {
      success
      error {
        message
      }
      columns {
        name
        exampleValues
      }
    }
  }

  mutation GetCsvColumnNumberRange($url: String!, $columnName: String!) {
    getCsvColumnNumberRange(url: $url, columnName: $columnName) {
      success
      error {
        message
      }
      numberRange {
        minValue
        maxValue
      }
    }
  }

  mutation MatchOrgGuestFeedbackCsvUploadLocations(
    $input: MatchOrgGuestFeedbackCsvUploadLocationsInput!
  ) {
    matchOrgGuestFeedbackCsvUploadLocations(input: $input) {
      success
      error {
        message
      }
      locationMappings {
        locationName
        locationId
      }
    }
  }

  query LocationsForGuestFeedback($pagination: PaginationInput) {
    Locations(pagination: $pagination) {
      objects {
        id
        name
      }
      totalCount
    }
  }

  query GuestFeedbackSourceOptions {
    OrgGuestFeedbackCsvUploads {
      objects {
        id
        feedbackSource
      }
    }
    OrgGuestFeedbackIntegrations {
      id
      ovationIntegrationConfig {
        id
      }
      googleBusinessConfig {
        id
      }
    }
  }
`;
